import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Pencil, Trash2, Copy, Save, Calendar, Tag,
  Clock, CheckCircle, XCircle, PauseCircle, Filter
} from "lucide-react";
import { ImageUploadField } from "@/components/admin/EditorComponents";
import { toast } from "sonner";
import axios from "axios";
import { getImageUrl } from "@/utils/imageUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  activa: { label: "Activa", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  programada: { label: "Programada", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock },
  finalizada: { label: "Finalizada", color: "bg-slate-100 text-slate-500 border-slate-200", icon: PauseCircle },
  desactivada: { label: "Desactivada", color: "bg-red-100 text-red-600 border-red-200", icon: XCircle },
  sin_fechas: { label: "Sin fechas", color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Calendar },
};

const CATEGORIES = [
  { value: "", label: "General" },
  { value: "mecanica", label: "Mecánica" },
  { value: "chapa", label: "Chapa y pintura" },
  { value: "recambios", label: "Recambios" },
  { value: "vehiculos", label: "Vehículos" },
];

const emptyForm = {
  title: "", description: "", image_url: "", start_date: "", end_date: "",
  active: true, link: "", category: "", highlight_text: "", order: 0, show_in_home: true
};

const toLocalDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; }
};

const toISO = (localDate) => {
  if (!localDate) return "";
  try { return new Date(localDate).toISOString(); } catch { return ""; }
};

export default function PromotionsPage() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [filterStatus, setFilterStatus] = useState("all");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/promotions`, { headers });
      setPromos(res.data);
    } catch { toast.error("Error al cargar promociones"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filterStatus === "all") return promos;
    return promos.filter(p => p.status === filterStatus);
  }, [promos, filterStatus]);

  const openNew = () => {
    setEditId("new");
    const now = new Date();
    const in30 = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    setForm({
      ...emptyForm,
      start_date: now.toISOString().slice(0, 16),
      end_date: in30.toISOString().slice(0, 16),
      order: promos.length + 1,
    });
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      ...p,
      start_date: toLocalDate(p.start_date),
      end_date: toLocalDate(p.end_date),
    });
  };

  const savePromo = async () => {
    if (!form.title) { toast.error("Titulo obligatorio"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Las fechas son obligatorias"); return; }
    const payload = {
      ...form,
      start_date: toISO(form.start_date),
      end_date: toISO(form.end_date),
    };
    try {
      if (editId === "new") {
        await axios.post(`${API}/promotions`, payload, { headers });
        toast.success("Promocion creada");
      } else {
        await axios.put(`${API}/promotions/${editId}`, payload, { headers });
        toast.success("Promocion actualizada");
      }
      setEditId(null);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  const deletePromo = async (id) => {
    try {
      await axios.delete(`${API}/promotions/${id}`, { headers });
      toast.success("Eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const duplicatePromo = async (id) => {
    try {
      await axios.post(`${API}/promotions/${id}/duplicate`, {}, { headers });
      toast.success("Duplicada");
      load();
    } catch { toast.error("Error al duplicar"); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/promotions/${p.id}`, { active: !p.active }, { headers });
      toast.success(p.active ? "Desactivada" : "Activada");
      load();
    } catch { toast.error("Error"); }
  };

  const counts = useMemo(() => {
    const c = { all: promos.length, activa: 0, programada: 0, finalizada: 0, desactivada: 0 };
    promos.forEach(p => { if (c[p.status] !== undefined) c[p.status]++; });
    return c;
  }, [promos]);

  if (loading) return <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="heading-industrial text-lg font-extrabold text-slate-900 uppercase tracking-wider">Promociones Postventa</h2>
          <p className="text-xs text-slate-500 mt-0.5">Gestion de ofertas y campanas con activacion automatica por fechas</p>
        </div>
        <Button onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-9 px-4" data-testid="add-promotion">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nueva promocion
        </Button>
      </div>

      {/* Status filter bar */}
      <div className="flex flex-wrap gap-1.5">
        {[
          { key: "all", label: "Todas" },
          { key: "activa", label: "Activas" },
          { key: "programada", label: "Programadas" },
          { key: "finalizada", label: "Finalizadas" },
          { key: "desactivada", label: "Desactivadas" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider border transition-all ${filterStatus === f.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
            data-testid={`filter-${f.key}`}>
            {f.label} <span className="text-[9px] opacity-60">({counts[f.key]})</span>
          </button>
        ))}
      </div>

      {/* Promotions list */}
      {filtered.length === 0 ? (
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="py-10 text-center text-sm text-slate-400">
            {filterStatus === "all" ? "No hay promociones. Crea la primera." : `No hay promociones con estado "${filterStatus}".`}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const st = STATUS_MAP[p.status] || STATUS_MAP.sin_fechas;
            const StIcon = st.icon;
            return (
              <Card key={p.id} className="border-slate-200 rounded-sm overflow-hidden" data-testid={`promo-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4 min-w-0">
                    {p.image_url && (
                      <div className="w-20 h-14 rounded-sm overflow-hidden bg-slate-100 flex-shrink-0">
                        <img src={getImageUrl(p.image_url)} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h3 className="text-sm font-bold text-slate-900 truncate min-w-0">{p.title}</h3>
                        <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border ${st.color} flex-shrink-0`}>
                          <StIcon className="w-3 h-3" /> {st.label}
                        </span>
                        {p.category && <span className="text-[9px] uppercase tracking-wider font-medium text-slate-400 border border-slate-200 px-1.5 py-0.5 rounded-sm flex-shrink-0">{p.category}</span>}
                        {p.show_in_home !== false && <span className="text-[9px] uppercase tracking-wider font-medium text-emerald-600 border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 rounded-sm flex-shrink-0">Home</span>}
                      </div>
                      {/* Iter 226 — Descripción contenida: máx 2 líneas, wrap por palabra/anywhere */}
                      {p.description && (
                        <p
                          className="text-xs text-slate-500 leading-snug line-clamp-2 [overflow-wrap:anywhere] break-words"
                          data-testid={`promo-desc-${p.id}`}
                          title={p.description.length > 120 ? p.description : undefined}
                        >
                          {p.description}
                        </p>
                      )}
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.start_date ? new Date(p.start_date).toLocaleDateString("es-ES") : "?"} - {p.end_date ? new Date(p.end_date).toLocaleDateString("es-ES") : "?"}</span>
                        {p.highlight_text && <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> {p.highlight_text}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-${p.id}`} />
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="h-7 w-7 p-0" data-testid={`edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => duplicatePromo(p.id)} className="h-7 w-7 p-0" data-testid={`dup-${p.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePromo(p.id)} className="h-7 w-7 p-0 text-red-500" data-testid={`del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit/Create dialog */}
      <Dialog open={editId !== null} onOpenChange={() => setEditId(null)}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="heading-industrial text-sm uppercase">{editId === "new" ? "Nueva promocion" : "Editar promocion"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Titulo</Label>
              <Input value={form.title || ""} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                className="rounded-sm h-8 border-slate-200 text-sm" placeholder="Ej: Revision pre-ITV 49.90" data-testid="promo-title-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Descripcion</Label>
              <textarea value={form.description || ""} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="flex min-h-[60px] w-full rounded-sm border border-slate-200 bg-transparent px-3 py-2 text-sm" rows={3}
                placeholder="Descripcion de la promocion..." data-testid="promo-desc-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Texto destacado (badge)</Label>
              <Input value={form.highlight_text || ""} onChange={e => setForm(p => ({ ...p, highlight_text: e.target.value }))}
                className="rounded-sm h-8 border-slate-200 text-sm" placeholder="Ej: -20%, Gratis, Desde 29.90" data-testid="promo-highlight-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Fecha de inicio</Label>
                <Input type="datetime-local" value={form.start_date || ""} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                  className="rounded-sm h-8 border-slate-200 text-sm" data-testid="promo-start-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Fecha de fin</Label>
                <Input type="datetime-local" value={form.end_date || ""} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                  className="rounded-sm h-8 border-slate-200 text-sm" data-testid="promo-end-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Categoria</Label>
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.map(c => (
                  <button key={c.value} onClick={() => setForm(p => ({ ...p, category: c.value }))}
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-sm border transition-all ${form.category === c.value ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Enlace (opcional)</Label>
              <Input value={form.link || ""} onChange={e => setForm(p => ({ ...p, link: e.target.value }))}
                className="rounded-sm h-8 border-slate-200 text-sm" placeholder="/cita o URL externa" data-testid="promo-link-input" />
            </div>
            <ImageUploadField value={form.image_url} onChange={v => setForm(p => ({ ...p, image_url: v }))} label="Imagen de la promocion" hint="Opcional. Se muestra junto al titulo." />
            <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
              <Label className="text-xs font-semibold text-slate-700">Activar manualmente</Label>
              <Switch checked={!!form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} data-testid="promo-active-toggle" />
              <span className="text-[9px] text-slate-400">Solo se publica si esta activa Y dentro del rango de fechas</span>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs font-semibold text-slate-700">Mostrar en Home</Label>
              <Switch checked={form.show_in_home !== false} onCheckedChange={v => setForm(p => ({ ...p, show_in_home: v }))} data-testid="promo-home-toggle" />
              <span className="text-[9px] text-slate-400">Si activa, aparece en la seccion de promociones de la pagina principal</span>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Orden</Label>
              <Input type="number" value={form.order ?? 0} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                className="rounded-sm h-8 border-slate-200 text-sm w-24" data-testid="promo-order-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditId(null)} className="rounded-sm text-xs uppercase h-8">Cancelar</Button>
            <Button onClick={savePromo} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold h-8" data-testid="promo-save-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
